/**
 * CulturePalette — dynamic culture color legend for the "crop fill" mode
 * (cropFillOn=true AND activeRasterMode='none').
 *
 * Bug A hotfix 2026-05-07. Founder verbatim:
 *   «Цвета легенды не совпадают с цветами на карте. Культуры в легенде
 *    выдуманы, они не растут в этом хозяйстве. Легенда заливки показывает
 *    строго только те культуры, которые есть в этом хозяйстве в выбранном
 *    сезоне.»
 *
 * Reverses Sprint 2026-21 lock-in #6 (hardcoded SYSTEM_CULTURE_COLORS).
 * Now fetches from `/api/v1/scope/crops?scope_type=&scope_id=` which reads
 * the same `field_visual_attrs` MV that feeds MVT tiles → colors match
 * byte-for-byte между легендой и заливкой.
 *
 * Хук `useScopeCrops` вынесен в `lib/map/useScopeCrops.ts` (AK-569): тот же
 * ответ читает попап поля, чтобы «Лён» в попапе и «Не разобрано» в легенде не
 * противоречили друг другу.
 *
 * Status patterns block оставлен как-есть (4 статуса по founder rules
 * 2026-05-07: planned/growing/harvested/fallow). Эти патерны — pure visual
 * штриховки, не зависят от реальных культур, так что hardcoded background
 * допустим.
 */
import { useMapStore } from '@/lib/map/useMapStoreV2';
import { useScopeCrops } from '@/lib/map/useScopeCrops';

interface StatusPattern {
  id: string;
  label: string;
  background: string;
}

// 4 statuses per founder lock-in 2026-05-07: planned / growing / harvested /
// fallow. (Old «Посеяно/Вегетация/Созревание» 5-status taxonomy is shelved
// pending future granularity; current MV emits exactly these 4 values.)
const STATUS_PATTERNS: StatusPattern[] = [
  {
    id: 'planned',
    label: 'Запланировано',
    background:
      'repeating-linear-gradient(45deg, #C2A878 0 4px, #8B7355 4px 8px)',
  },
  {
    id: 'growing',
    label: 'Растёт',
    background: '#7AAB3D',
  },
  {
    id: 'harvested',
    label: 'Убрано',
    background:
      'repeating-linear-gradient(90deg, #C2A878 0 4px, #8B7355 4px 8px)',
  },
  {
    id: 'fallow',
    label: 'Пар',
    background:
      'repeating-linear-gradient(135deg, #8B7355 0 4px, #5D4E37 4px 8px)',
  },
];

export function CulturePalette() {
  const activeScope = useMapStore((s) => s.activeScope);
  const { data: crops, isLoading, isError } = useScopeCrops(activeScope);

  return (
    <div data-testid="culture-palette" className="py-1">
      <div className="px-2 py-1 border-b border-border/60 text-xs text-center font-semibold text-foreground">
        Культуры
      </div>
      <div className="py-1">
        {isLoading && (
          <div
            data-testid="culture-palette-loading"
            className="px-2 py-1 text-xs text-muted-foreground italic"
          >
            Загрузка…
          </div>
        )}
        {!isLoading && isError && (
          <div
            data-testid="culture-palette-error"
            className="px-2 py-1 text-xs text-destructive"
          >
            Не удалось загрузить
          </div>
        )}
        {!isLoading && !isError && (crops?.length ?? 0) === 0 && (
          <div
            data-testid="culture-palette-empty"
            className="px-2 py-1 text-xs text-muted-foreground italic"
          >
            Нет культур в этом scope
          </div>
        )}
        {!isLoading &&
          !isError &&
          (crops ?? []).map((c) => (
            <div
              key={c.crop_id ?? `unknown-${c.color}`}
              data-testid={c.crop_id ? `crop-${c.crop_id}` : 'crop-unknown'}
              className="flex items-center gap-1 px-2 py-1"
            >
              <span
                className="w-3.5 h-3.5 rounded-sm shrink-0"
                style={{ background: c.color }}
                aria-hidden
              />
              <span className="text-xs text-muted-foreground">{c.name}</span>
            </div>
          ))}
      </div>
      {/* Status patterns (4 rows per founder lock-in 2026-05-07) */}
      <div className="border-t border-border/60 py-1">
        <div className="px-2 py-1 text-xs text-center font-semibold text-muted-foreground">
          Статус
        </div>
        {STATUS_PATTERNS.map((p) => (
          <div
            key={p.id}
            data-testid={`status-pattern-${p.id}`}
            className="flex items-center gap-1 px-2 py-1"
          >
            <span
              className="w-3.5 h-3.5 rounded-sm shrink-0 border border-border/40"
              style={{ background: p.background }}
              aria-hidden
            />
            <span className="text-xs text-muted-foreground">{p.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CulturePalette;
